import {
  switchMap,
  filter,
  map,
  tap,
  withLatestFrom,
  catchError
} from 'rxjs/operators'
import { AppState } from 'src/app/core/core.state'
import { environment } from 'src/environments/environment'

import { Injectable } from '@angular/core'
import { Actions, createEffect, ofType, OnInitEffects } from '@ngrx/effects'
import { Store } from '@ngrx/store'

import { oidcActions } from './oidc.actions'
import { oidcSelectors } from './oidc.selectors'
import { OidcService } from './oidc.service'
import { noOperationAction } from 'src/app/shared/resource-effects'
import { of } from 'rxjs'

@Injectable()
export class OidcEffects implements OnInitEffects {
  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private oidcService: OidcService
  ) {}

  ngrxOnInitEffects() {
    return oidcActions.initProviderSettings()
  }

  initProviderSettings = createEffect(() =>
    this.actions$.pipe(
      ofType(oidcActions.initProviderSettings),
      withLatestFrom(this.store.select(oidcSelectors.selectProviderSettings)),
      filter(([action, loadedProviderSettings]) => {
        if (loadedProviderSettings) {
          console.log(
            'Oidc initProviderSettings wont fetch provider settings as it loaded',
            loadedProviderSettings
          )
          return false
        }
        return true
      }),
      map(([action, loadedProviderSettings]) => {
        let providerSettings = environment.oidcProviderSettings.find(
          (x) => x.name == environment.defaultOidcProvider
        )

        if (!providerSettings) {
          providerSettings = environment.oidcProviderSettings[0]
        }

        console.log(
          'Oidc will fetch provider config for provider settings',
          providerSettings
        )

        return oidcActions.loadProviderSettings({
          providerSettings
        })
      }),
      catchError((error) => {
        console.error('Error during initProviderSettings', error)
        return of(noOperationAction())
      })
    )
  )

  fetchProviderConfig = createEffect(() =>
    this.actions$.pipe(
      ofType(oidcActions.fetchProviderConfig, oidcActions.loadProviderSettings),
      filter((action) => Boolean(action.providerSettings)),
      switchMap((action) =>
        this.oidcService.fetchProviderConfig(action.providerSettings)
      ),
      map((providerConfig) =>
        oidcActions.loadProviderConfig({ providerConfig })
      )
    )
  )

  startAuth = createEffect(
    () =>
      this.actions$.pipe(
        ofType(oidcActions.startAuth),
        withLatestFrom(
          this.store.select(oidcSelectors.selectProviderSettings),
          this.store.select(oidcSelectors.selectProviderConfig)
        ),
        tap(([action, providerSettings, providerConfig]) =>
          this.oidcService.makeAuthorizationRequest(
            providerSettings,
            providerConfig
          )
        )
      ),
    { dispatch: false }
  )

  fetchTokenResponseByCode = createEffect(
    () =>
      this.actions$.pipe(
        ofType(oidcActions.fetchTokenResponseByCode),
        withLatestFrom(
          this.store.select(oidcSelectors.selectProviderSettings),
          this.store.select(oidcSelectors.selectProviderConfig)
        ),
        tap(async ([action, providerSettings, providerConfig]) => {
          const tokenResponse = await this.oidcService.fetchTokenResponseByCode(
            providerSettings,
            providerConfig,
            action.authCodeResponse
          )

          if (tokenResponse) {
            this.store.dispatch(
              oidcActions.loadTokenResponse({ tokenResponse })
            )
          }
        })
      ),
    { dispatch: false }
  )

  fetchTokenResponseByRefreshToken = createEffect(
    () =>
      this.actions$.pipe(
        ofType(oidcActions.fetchTokenResponseByRefreshToken),
        withLatestFrom(
          this.store.select(oidcSelectors.selectProviderSettings),
          this.store.select(oidcSelectors.selectProviderConfig)
        ),
        filter(([action, providerSettings, providerConfig]) =>
          Boolean(action.refreshToken)
        ),
        tap(async ([action, providerSettings, providerConfig]) => {
          console.log(
            'fetchTokenResponseByRefreshToken will now attempt oidc token refresh'
          )

          const tokenResponse =
            await this.oidcService.fetchTokenResponseByRefreshToken(
              providerSettings,
              providerConfig,
              action.refreshToken
            )

          if (tokenResponse) {
            console.log(
              'fetchTokenResponseByRefreshToken got valid token response'
            )
            this.store.dispatch(
              oidcActions.loadTokenResponse({ tokenResponse })
            )
          } else {
            console.log(
              'fetchTokenResponseByRefreshToken got no valid oidc token response and should logout'
            )
          }
        })
      ),
    { dispatch: false }
  )
}
